import { registerBlockType } from '@wordpress/blocks';
import { useBlockProps, RichText, InspectorControls, InnerBlocks, MediaUpload, MediaUploadCheck } from '@wordpress/block-editor';
import { PanelBody, Button } from '@wordpress/components';
import { Fragment, useEffect } from '@wordpress/element';
import { useSelect } from '@wordpress/data';
import './block-styles.css';

registerBlockType('custom-blocks/service-showcase', {
    title: 'Service Showcase',
    icon: 'schedule',
    category: 'custom-blocks',
    attributes: {
        title: {
            type: 'string',
            source: 'html',
            selector: 'h2',
            default: 'Main Title',
        },
        subtitle: {
            type: 'string',
            source: 'html',
            selector: 'h3',
            default: 'Subtitle',
        },
        description: {
            type: 'string',
            source: 'html',
            selector: 'p',
            default: 'Description',
        },
        tabDetails: {
            type: 'object',
            default: {},
        },
        sliderImages: {
            type: 'array',
            default: [],
            source: 'query',
            selector: '.swiper-slide img',
            query: {
                url: {
                    type: 'string',
                    source: 'attribute',
                    attribute: 'src',
                },
                alt: {
                    type: 'string',
                    source: 'attribute',
                    attribute: 'alt',
                },
            },
        },
    },
    edit: ({ attributes, setAttributes, clientId }) => {
        const { title, subtitle, description, tabDetails, sliderImages } = attributes;
        const blockProps = useBlockProps();

        const childBlocks = useSelect((select) =>
            select('core/block-editor').getBlocks(clientId)
        );

        // Crear la lista de tabs basados en los datos de los bloques hijos
        useEffect(() => {
            const newTabDetails = {};
            childBlocks.forEach((child) => {
                const { tabTitle, icon } = child.attributes;
                newTabDetails[child.clientId] = { tabTitle, icon };
            });
            setAttributes({ tabDetails: newTabDetails });
        }, [childBlocks]); // Actualiza tabDetails cuando cambian los childBlocks

        const onSelectImage = (image, index) => {
            const newImages = [...sliderImages];
            newImages[index] = { url: image.url, alt: image.alt };
            setAttributes({ sliderImages: newImages });
        };

        const onRemoveImage = (index) => {
            const newImages = [...sliderImages];
            newImages.splice(index, 1); // Elimina la imagen del array
            setAttributes({ sliderImages: newImages });
        };

        return (
            <Fragment>
                <InspectorControls>
                    <PanelBody title="Showcase Settings">
                        <RichText
                            tagName="h3"
                            value={subtitle}
                            onChange={(value) => setAttributes({ subtitle: value })}
                            placeholder="Enter subtitle"
                        />
                        <RichText
                            tagName="h2"
                            value={title}
                            onChange={(value) => setAttributes({ title: value })}
                            placeholder="Enter title"
                        />
                        <RichText
                            tagName="p"
                            value={description}
                            onChange={(value) => setAttributes({ description: value })}
                            placeholder="Enter description"
                        />
                    </PanelBody>
                    <PanelBody title="Slider Images">
                        {sliderImages.map((image, index) => (
                            <div key={index} className="slider-image-control">
                                <MediaUploadCheck>
                                    <MediaUpload
                                        onSelect={(img) => onSelectImage(img, index)}
                                        allowedTypes={['image']}
                                        render={({ open }) => (
                                            <Button onClick={open} className="slider-image-preview">
                                                {image.url ? (
                                                    <img src={image.url} alt={image.alt} />
                                                ) : (
                                                    'Select Image'
                                                )}
                                            </Button>
                                        )}
                                    />
                                </MediaUploadCheck>
                                <Button
                                    onClick={() => onRemoveImage(index)}
                                    isDestructive
                                >
                                    Remove
                                </Button>
                            </div>
                        ))}
                        <Button
                            onClick={() => setAttributes({ sliderImages: [...sliderImages, {}] })}
                            isPrimary
                        >
                            Add Slide
                        </Button>
                    </PanelBody>
                </InspectorControls>
                <div {...blockProps}>
                    <div className="ssc-wrapper">
                        <div className="ssc-column">
                            <div className="content">
                                <h3>{subtitle}</h3>
                                <h2>{title}</h2>
                                <p>{description}</p>
                                <ul className="ssc-tab-option">
                                    {Object.entries(tabDetails).map(([clientId, details]) => (
                                        <li key={clientId}>
                                            <button>
                                                {details.icon && <img src={details.icon} alt="Tab Icon" />}
                                                <span>{details.tabTitle}</span>
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                                <div className="swiper-container-custom">
                                    <div className="swiper-wrapper">
                                        {sliderImages.map((image, index) => (
                                            <div className="swiper-slide" key={index}>
                                                <img src={image.url} alt={image.alt} />
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="ssc-column">
                            <div className="ssc-tab-content-wrapper">
                                <div className="content">
                                    <InnerBlocks
                                        allowedBlocks={['custom-blocks/service-showcase-tab-content']}
                                        templateLock={false}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </Fragment>
        );
    },
    save: ({ attributes }) => {
        const { title, subtitle, description, tabDetails, sliderImages } = attributes;
        const blockProps = useBlockProps.save();

        return (
            <div {...blockProps}>
                <div className="ssc-wrapper">
                    <div className="ssc-column">
                        <div className="content">
                            <h3>{subtitle}</h3>
                            <h2>{title}</h2>
                            <p>{description}</p>
                            <ul className="ssc-tab-option">
                                {Object.entries(tabDetails).map(([clientId, details], index) => (
                                    <li key={clientId}>
                                        <button className={`tab-button`} data-tab={`tab-${index}`}>
                                            {details.icon && <img src={details.icon} alt="Tab Icon" />}
                                            <span>{details.tabTitle}</span>
                                        </button>
                                    </li>
                                ))}
                            </ul>
                            <div className="swiper-container-custom">
                                <div className="swiper-wrapper">
                                    {sliderImages.map((image, index) => (
                                        <div className="swiper-slide" key={index}>
                                            <img src={image.url} alt={image.alt} />
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="ssc-column">
                        <div className="ssc-tab-content-wrapper">
                            <div className="content">
                                {Object.entries(tabDetails).map(([clientId], index) => (
                                    <div className={`tab-content`} id={`tab-${index}`} key={`content-${clientId}`}>
                                        <InnerBlocks.Content />
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    },
});
